/**
 * Pairing Service - Serviço de Pareamento Companion/Player
 * Módulo Motorista - Sistema IDOOH
 */

import { supabase } from '@/integrations/supabase/client';

// ============================================================
// TIPOS
// ============================================================

export interface DevicePairing {
    id: string;
    pairing_code: string;
    device_id: string | null;
    driver_id: string | null;
    status: 'pending' | 'active' | 'inactive';
    paired_at: string | null;
    last_seen_at: string | null;
    created_at: string;
    updated_at: string;
}

export interface PairingResult {
    success: boolean;
    message?: string;
    error?: string;
    device_id?: string;
}

export interface CreatePairingResult {
    pairing_code: string;
    id: string;
}

// ============================================================
// FUNÇÕES DO COMPANION (Celular do Motorista)
// ============================================================

/**
 * Ativa um tablet usando o código de pareamento
 * Usa a função RPC do banco com SECURITY DEFINER para contornar RLS
 * @param pairingCode - Código de 8 caracteres exibido no tablet
 * @param driverId - ID do motorista logado
 */
export async function activatePairing(
    pairingCode: string,
    driverId: string
): Promise<PairingResult> {
    try {
        const cleanCode = pairingCode.toUpperCase().trim();
        console.log('[PairingService] Tentando ativar código via RPC:', cleanCode);

        // Usar a função RPC do banco que tem SECURITY DEFINER
        // Isso contorna as limitações de RLS para registros inativos
        const { data: rpcResult, error: rpcError } = await supabase.rpc('activate_pairing', {
            p_pairing_code: cleanCode,
            p_driver_id: driverId
        });

        if (rpcError) {
            console.error('[PairingService] Erro RPC:', rpcError);
            return { success: false, error: 'Erro ao ativar tablet. Tente novamente.' };
        }

        console.log('[PairingService] Resultado RPC:', rpcResult);

        // O resultado é um JSON com {success, message/error, device_id}
        if (rpcResult && rpcResult.success) {
            console.log('[PairingService] ✅ Tablet ativado com sucesso via RPC!');
            return {
                success: true,
                message: rpcResult.message || 'Tablet ativado com sucesso!',
                device_id: rpcResult.device_id
            };
        } else {
            const errorMsg = rpcResult?.error || 'Erro desconhecido ao ativar tablet';
            console.error('[PairingService] Falha na ativação:', errorMsg);
            return { success: false, error: errorMsg };
        }

    } catch (error) {
        console.error('[PairingService] Erro inesperado:', error);
        return { success: false, error: 'Erro de conexão. Verifique sua internet.' };
    }
}

/**
 * Lista os tablets pareados do motorista (apenas ativos)
 * @param driverId - ID do motorista
 */
export async function getDriverPairings(driverId: string): Promise<DevicePairing[]> {
    try {
        const { data, error } = await supabase
            .from('device_pairings')
            .select('*')
            .eq('driver_id', driverId)
            .eq('status', 'active')  // Mostrar apenas ativos
            .order('created_at', { ascending: false });

        if (error) {
            console.error('[PairingService] Erro ao buscar pareamentos:', error);
            return [];
        }

        return data as DevicePairing[];
    } catch (error) {
        console.error('[PairingService] Erro inesperado:', error);
        return [];
    }
}

/**
 * Desativa um pareamento (define status como inactive e remove driver_id)
 * NOTA: Usamos UPDATE em vez de DELETE porque a política RLS não permite DELETE
 * @param pairingId - ID do pareamento
 */
export async function deactivatePairing(pairingId: string): Promise<boolean> {
    try {
        console.log('[PairingService] Desativando pareamento:', pairingId);

        const { data, error } = await supabase
            .from('device_pairings')
            .update({
                status: 'inactive',
                driver_id: null,
                paired_at: null,
                updated_at: new Date().toISOString()
            })
            .eq('id', pairingId)
            .select();

        if (error) {
            console.error('[PairingService] Erro ao desativar:', error);
            return false;
        }

        if (!data || data.length === 0) {
            console.error('[PairingService] Nenhum registro atualizado');
            return false;
        }

        console.log('[PairingService] ✅ Pareamento desativado com sucesso:', data);
        return true;
    } catch (error) {
        console.error('[PairingService] Erro inesperado:', error);
        return false;
    }
}

// ============================================================
// FUNÇÕES DO PLAYER (Tablet no Veículo)
// ============================================================

/**
 * Gera código de 8 caracteres alfanuméricos
 */
function generateLocalCode(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
}

/**
 * Cria ou recupera um pareamento pendente para o tablet
 * @param deviceId - ID único do dispositivo (pode ser gerado localmente)
 */
export async function createOrGetPairing(deviceId: string): Promise<CreatePairingResult | null> {
    try {
        // Primeiro, verificar se já existe pareamento para este device
        const { data: existing, error: selectError } = await supabase
            .from('device_pairings')
            .select('id, pairing_code')
            .eq('device_id', deviceId)
            .in('status', ['pending', 'active'])
            .limit(1)
            .single();

        if (existing && !selectError) {
            console.log('[PairingService] Pareamento existente encontrado:', existing.pairing_code);
            return {
                pairing_code: existing.pairing_code,
                id: existing.id
            };
        }

        // Se não existe, criar novo
        const newCode = generateLocalCode();
        console.log('[PairingService] Gerando novo código:', newCode);

        const { data: inserted, error: insertError } = await supabase
            .from('device_pairings')
            .insert({
                pairing_code: newCode,
                device_id: deviceId,
                status: 'pending'
            })
            .select('id, pairing_code')
            .single();

        if (insertError) {
            console.error('[PairingService] Erro ao inserir pareamento:', insertError);

            // Se o código já existe (colisão rara), tentar novamente
            if (insertError.code === '23505') {
                console.log('[PairingService] Colisão de código, tentando novamente...');
                return createOrGetPairing(deviceId);
            }
            return null;
        }

        console.log('[PairingService] Pareamento criado:', inserted);
        return {
            pairing_code: inserted.pairing_code,
            id: inserted.id
        };

    } catch (error) {
        console.error('[PairingService] Erro inesperado:', error);
        return null;
    }
}

/**
 * Verifica o status do pareamento do tablet
 * @param pairingCode - Código de pareamento
 */
export async function checkPairingStatus(pairingCode: string): Promise<DevicePairing | null> {
    try {
        const { data, error } = await supabase
            .from('device_pairings')
            .select('*')
            .eq('pairing_code', pairingCode.toUpperCase())
            .single();

        if (error) {
            console.error('[PairingService] Erro ao verificar status:', error);
            return null;
        }

        return data as DevicePairing;
    } catch (error) {
        console.error('[PairingService] Erro inesperado:', error);
        return null;
    }
}

/**
 * Atualiza o last_seen_at do tablet (heartbeat)
 * @param pairingId - ID do pareamento
 */
export async function updateLastSeen(pairingId: string): Promise<void> {
    try {
        await supabase
            .from('device_pairings')
            .update({ last_seen_at: new Date().toISOString() })
            .eq('id', pairingId);
    } catch (error) {
        console.error('[PairingService] Erro ao atualizar last_seen:', error);
    }
}

// ============================================================
// FUNÇÕES AUXILIARES
// ============================================================

/**
 * Gera um ID único para o dispositivo (usado pelo tablet)
 */
export function generateDeviceId(): string {
    const stored = localStorage.getItem('driver_tablet_device_id');
    if (stored) return stored;

    const newId = `tablet_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem('driver_tablet_device_id', newId);
    return newId;
}

/**
 * Formata o código de pareamento para exibição (XXXX-XXXX)
 */
export function formatPairingCode(code: string): string {
    if (!code) return code;
    // Formato para 8 dígitos: XXXX-XXXX
    if (code.length === 8) {
        return `${code.substring(0, 4)}-${code.substring(4)}`;
    }
    // Formato para 6 dígitos (compatibilidade): XXX-XXX
    if (code.length === 6) {
        return `${code.substring(0, 3)}-${code.substring(3)}`;
    }
    return code;
}

/**
 * Remove formatação do código de pareamento
 */
export function cleanPairingCode(code: string): string {
    return code.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}
